// SPDX-License-Identifier: AGPL-3.0-or-later

import * as Modal from '@app/features/app/components/dialogs/Modal';
import styles from '@app/features/discovery/components/modals/SubmitThemeModal.module.css';
import ThemeDiscoveryState, {type ThemeCategory} from '@app/features/discovery/state/ThemeDiscoveryState';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useState} from 'react';

export const SubmitThemeModal: React.FC = observer(() => {
	const [name, setName] = useState('');
	const [author, setAuthor] = useState('');
	const [description, setDescription] = useState('');
	const [category, setCategory] = useState<ThemeCategory>('dark');
	const [c1, setC1] = useState('#0f172a');
	const [c2, setC2] = useState('#1e293b');
	const [c3, setC3] = useState('#38bdf8');
	const [c4, setC4] = useState('#f8fafc');
	const [css, setCss] = useState(`:root {
  --background-primary: #0f172a !important;
  --background-secondary: #1e293b !important;
  --brand-experiment: #38bdf8 !important;
}`);
	const [submitting, setSubmitting] = useState(false);

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		if (!name.trim() || !description.trim() || !css.trim()) {
			return;
		}
		setSubmitting(true);

		const id = `custom-theme-${Date.now()}`;
		ThemeDiscoveryState.submitTheme({
			id,
			name: name.trim(),
			description: description.trim(),
			author: author.trim() || 'Community Creator',
			category,
			previewColors: [c1, c2, c3, c4],
			css: css.trim(),
			tags: [category, 'community-theme'],
		});

		ToastCommands.createToast({
			type: 'success',
			children: `Theme "${name}" submitted to Discovery! Under review by NDC Admin staff.`,
		});

		ModalCommands.pop();
	};

	return (
		<Modal.Root size="medium" centered onClose={ModalCommands.pop} data-flx="discovery.submit-theme-modal">
			<div className={styles.container}>
				<h2 className={styles.title}>Submit a Theme to Discovery</h2>
				<p className={styles.subtitle}>
					Share your custom visual theme with all Nexoria users. Provide a palette preview and CSS variables. Once approved by NDC Admin staff, your theme will be publicly available in the Theme Store.
				</p>

				<form onSubmit={handleSubmit} className={styles.form}>
					<div className={styles.fieldGroup}>
						<label className={styles.label}>Theme Name</label>
						<input
							type="text"
							value={name}
							onChange={(e) => setName(e.target.value)}
							placeholder="e.g. Deep Ocean Horizon"
							className={styles.input}
							required
						/>
					</div>

					<div className={styles.fieldGroup}>
						<label className={styles.label}>Author / Creator Handle</label>
						<input
							type="text"
							value={author}
							onChange={(e) => setAuthor(e.target.value)}
							placeholder="e.g. NightOwl"
							className={styles.input}
							required
						/>
					</div>

					<div className={styles.fieldGroup}>
						<label className={styles.label}>Category</label>
						<select
							value={category}
							onChange={(e) => setCategory(e.target.value as ThemeCategory)}
							className={styles.select}
						>
							<option value="dark">Dark & OLED</option>
							<option value="neon">Neon & Glow</option>
							<option value="minimal">Clean Minimal</option>
							<option value="retro">Retro & Synthwave</option>
							<option value="colorful">Colorful & Pastel</option>
						</select>
					</div>

					<div className={styles.fieldGroup}>
						<label className={styles.label}>Short Description</label>
						<input
							type="text"
							value={description}
							onChange={(e) => setDescription(e.target.value)}
							placeholder="e.g. Soothing aquatic hues with deep sapphire backdrop."
							className={styles.input}
							required
						/>
					</div>

					<div className={styles.fieldGroup}>
						<label className={styles.label}>Color Swatch Palette Preview (4 colors)</label>
						<div className={styles.colorInputsRow}>
							<div className={styles.colorPickerBox}>
								<input type="color" value={c1} onChange={(e) => setC1(e.target.value)} className={styles.colorInput} />
								<span style={{fontSize: '0.8rem', fontFamily: 'monospace'}}>{c1}</span>
							</div>
							<div className={styles.colorPickerBox}>
								<input type="color" value={c2} onChange={(e) => setC2(e.target.value)} className={styles.colorInput} />
								<span style={{fontSize: '0.8rem', fontFamily: 'monospace'}}>{c2}</span>
							</div>
							<div className={styles.colorPickerBox}>
								<input type="color" value={c3} onChange={(e) => setC3(e.target.value)} className={styles.colorInput} />
								<span style={{fontSize: '0.8rem', fontFamily: 'monospace'}}>{c3}</span>
							</div>
							<div className={styles.colorPickerBox}>
								<input type="color" value={c4} onChange={(e) => setC4(e.target.value)} className={styles.colorInput} />
								<span style={{fontSize: '0.8rem', fontFamily: 'monospace'}}>{c4}</span>
							</div>
						</div>
					</div>

					<div className={styles.fieldGroup}>
						<label className={styles.label}>Theme CSS Variables</label>
						<textarea
							value={css}
							onChange={(e) => setCss(e.target.value)}
							className={styles.textarea}
							required
						/>
					</div>

					<div className={styles.actions}>
						<button type="button" onClick={ModalCommands.pop} className={styles.cancelBtn}>
							Cancel
						</button>
						<button
							type="submit"
							disabled={submitting || !name.trim() || !description.trim() || !css.trim()}
							className={styles.submitBtn}
						>
							{submitting ? 'Submitting...' : 'Submit Theme'}
						</button>
					</div>
				</form>
			</div>
		</Modal.Root>
	);
});
